/**
 * Monaco's registry declaration.
 *
 * A hand-written manifest rather than an inlined `manifest.json`, for the same
 * reason `builtinCanvasExtension.ts` has one: there is no extension package to
 * read it from. Monaco is core, shipped from `@nimbalyst/runtime`.
 *
 * `extensionId` is not a free choice. `WebCollabHost` already stamps
 * `editorId: 'builtin.monaco'` into the metadata of any `code` document created
 * here, and the desktop catalog records the same id for its Monaco descriptor.
 * A different id would leave every existing code document resolving to an
 * editor nothing registers.
 *
 * The suffix list comes from the shared `CODE_COLLAB_FILE_EXTENSIONS` rather
 * than a literal. It has to be readable *before* any bundle loads, since the
 * catalog answers "can this console open a `.ts` document" from the manifest
 * alone -- but it must be the same list the desktop claims, or the two hosts
 * disagree about which files are code. Importing the constant is cheap: it is a
 * leaf module of plain data and pulls no Monaco with it.
 *
 * `code` deliberately does not appear in `newFileMenu`. Nothing in the browser
 * seeds a new room from a manifest's default content, so a menu entry would
 * advertise a creation path that does not exist.
 */

import { CODE_COLLAB_FILE_EXTENSIONS } from '@nimbalyst/runtime-source/editors/monacoLanguages';

import { registerBrowserExtension, type BrowserExtensionManifest } from '@nimbalyst/web-editors/registry';

export const BUILTIN_MONACO_EXTENSION_ID = 'builtin.monaco';
export const BUILTIN_MONACO_COMPONENT = 'MonacoCodeDocumentEditor';

export const BUILTIN_MONACO_MANIFEST: BrowserExtensionManifest = {
  id: BUILTIN_MONACO_EXTENSION_ID,
  name: 'Code',
  contributions: {
    customEditors: [
      {
        filePatterns: CODE_COLLAB_FILE_EXTENSIONS.map((suffix) => `*${suffix}`),
        displayName: 'Code',
        component: BUILTIN_MONACO_COMPONENT,
        collaboration: { supported: true, documentType: 'code' },
      },
    ],
  },
  marketplace: { icon: 'code' },
};

/**
 * Declares the manifest only. Monaco itself -- the largest thing this console
 * can load -- arrives with the lazy import the first time a code document is
 * opened, and never for a session that opens none.
 */
export function registerBuiltinMonacoExtension(): void {
  registerBrowserExtension({
    manifest: BUILTIN_MONACO_MANIFEST,
    load: () => import('./webMonacoBundle'),
  });
}
